/**
 * Template Specification for "bar" Chart
 *
 * This template defines the structure and default configuration for a "bar" chart.
 *
 * Key Categories:
 * 1. Standard Fields:
 *    - 'type': Specifies the chart type (here, "bar").
 *    - 'metric' and 'title': Define the primary data fields.
 *      Their values start with "$", indicating that these fields expect dynamic inputs
 *      (e.g., a metric selection or a text string).
 *
 * 2. Label Fields:
 *    - Keys prefixed with a single underscore (e.g., '_type', '_metric', '_title')
 *      provide human-readable labels for the corresponding standard fields.
 *
 * 3. Default / Dependency Fields:
 *    - Keys prefixed with double underscores (e.g., '__title') specify default values or dependencies.
 *      A value starting with "$$" (e.g., '__title': '$$metric') sets up a dependency, where the
 *      title dynamically reflects the name of the metric selected for the 'metric' field.
 *
 * This specification enables flexible configuration by combining dynamic inputs,
 * dependencies, and user-friendly labels, which together facilitate easy customization
 * and initialization of a bar chart.
 */
export const bar = {
    'type': 'bar_chart',
    '_type': 'Bar',
    'metric': '$metric',
    '_metric': 'Metric',
    'title': "$string",
    '_title': 'Title',
    '__title': '$$metric',
}

// Example chart configuration for "bar" chart. Used in preview mode
export const barExample = (title, metric_label) => ({
    "chart": {
        "type": "column",
        "zoomType": "xy",
        "events": {},
        "height": "100%"
    },
    "drilldown": {
        "activeAxisLabelStyle": {
            "cursor": "pointer",
            "color": "rgb(51, 51, 51)",
            "textDecoration": "none",
            "fontWeight": 500,
            "tickmarkPlacement": "between",
            "tickWidth": 1
        },
        "activeDataLabelStyle": {
            "cursor": "pointer",
            "color": "rgb(51, 51, 51)",
            "textDecoration": "none"
        },
        "series": []
    },
    "title": {
        "text": title
    },
    "xAxis": {
        "categories": [
            "03-04",
            "03-06",
            "03-08"
        ],
        "gridLineWidth": 2,
        "lineColor": "#ccd6eb",
        "lineWidth": 1,
        "labels": {
            "rotation": 0
        },
        "tickmarkPlacement": "between",
        "tickWidth": 1
    },
    "yAxis": [
        {
            "title": {
                "text": ""
            },
            "min": 0,
            "max": 1675,
            "tickAmount": 6,
            "plotLines": []
        },
        {
            "title": {
                "text": ""
            },
            "min": null,
            "max": 0,
            "opposite": true,
            "gridLineWidth": 0,
            "id": "acwr",
            "plotLines": [
                {
                    "id": "acwr_upper",
                    "dashStyle": "ShortDash",
                    "color": "red",
                    "value": 1.3,
                    "zIndex": 5
                },
                {
                    "id": "acwr_lower",
                    "dashStyle": "ShortDash",
                    "color": "green",
                    "value": 0.8,
                    "zIndex": 5
                }
            ]
        }
    ],
    "legend": {
        "enabled": true,
        "verticalAlign": "top",
        "layout": "horizontal"
    },
    "tooltip": {
        "shared": true
    },
    "plotOptions": {
        "column": {
            "borderWidth": 0,
            "groupPadding": 0.05,
            "pointPadding": 0.05,
            "dataLabels": {
                "enabled": true,
                "rotation": 0,
                "y": 0,
                "inside": true
            }
        }
    },
    "colors": [
        "#31B452",
        "#FDB515",
        "#3B7EA1",
        "#C4820E",
        "#4797ff",
        "#f526fA"
    ],
    "series": [
        {
            "name": metric_label,
            "type": "column",
            "yAxis": 0,
            "data": [
                {
                    "y": 1058.1,
                    "color": "#31B452",
                    "restTravelLabel": "",
                    "isRestOrTravel": false,
                    "drilldown": "03-04",
                    "name": "03-04"
                },
                {
                    "y": 1522.1,
                    "color": "#31B452",
                    "restTravelLabel": "",
                    "isRestOrTravel": false,
                    "drilldown": "03-06",
                    "name": "03-06"
                },
                {
                    "y": 1284.5,
                    "color": "#31B452",
                    "restTravelLabel": "",
                    "isRestOrTravel": false,
                    "drilldown": "03-08",
                    "name": "03-08"
                }
            ],
            "colorByPoint": false,
            "dataLabels": {
                "style": {
                    "textOutline": "none"
                }
            }
        },
        {
            "dataLabels": {
                "style": {
                    "textOutline": "none"
                }
            },
            "name": "ACWR",
            "type": "spline",
            "yAxis": 1,
            "data": [
                null,
                null,
                null
            ],
            "color": "rgb(70, 170, 197)",
            "marker": {
                "enabled": true,
                "radius": 3
            },
            "selected": true,
            "showInLegend": false,
            "connectNulls": true
        }
    ],
    "exporting": {
        "enabled": false
    }
})